<template>
  <section class="view-stack">
    <div class="section-actions">
      <el-button type="success" icon="el-icon-wallet" round @click="$router.push({ name: 'dashboardBilling' })">
        充值
      </el-button>
      <el-button type="primary" icon="el-icon-plus" round @click="openCreateDialog">
        创建密钥
      </el-button>
    </div>

    <article class="dashboard-panel">
      <div class="panel-heading">
        <div>
          <h2>API 密钥</h2>
          <p>管理您的 API 密钥，支持一键导入 CC Switch，也可以一键生成 Codex 配置文件与安装脚本。</p>
        </div>
      </div>

      <div class="key-filter-bar">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索名称或 Key..."
        ></el-input>
        <el-select v-model="filters.balanceId" clearable placeholder="全部分组">
          <el-option
            v-for="item in queryGroupOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <div class="usage-filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
          <el-button icon="el-icon-data-analysis" @click="openModelsDialog">
            模型与价格
          </el-button>
          <el-button type="primary" plain icon="el-icon-chat-dot-round" @click="openModelTestPage">
            测试模型
          </el-button>
        </div>
      </div>

      <el-table
        :key="tableKey"
        v-loading="loading"
        :data="apiKeys"
        class="dashboard-table api-key-table"
        empty-text="暂无 API 密钥"
      >
        <el-table-column prop="keyName" label="名称" width="140" sortable show-overflow-tooltip></el-table-column>
        <el-table-column label="API 密钥" width="220">
          <template slot-scope="scope">
            <span class="api-key-text">{{ maskApiKey(scope.row.apiKey) }}</span>
            <el-button
              type="text"
              icon="el-icon-copy-document"
              @click="copyText(scope.row.apiKey, '密钥已复制')"
            ></el-button>
          </template>
        </el-table-column>
        <el-table-column label="分组" min-width="240" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-tag :type="scope.row.groupType === 'MONTH_CARD' ? 'warning' : 'success'" size="small">
              {{ getKeyGroupLabel(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="用量" width="170">
          <template slot-scope="scope">
            <div class="key-usage-cell">
              <span>今日：{{ formatAmount(scope.row.usedAmount) }}</span>
              <span>近 30 天：{{ formatAmount(scope.row.recent30DayUsedAmount) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170" sortable>
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="390" fixed="right">
          <template slot-scope="scope">
            <div class="key-action-row">
              <el-button size="mini" type="success" icon="el-icon-key" @click="openKeyUsageDialog(scope.row)">
                一键配置 Codex
              </el-button>
              <el-button size="mini" type="primary" icon="el-icon-upload2" @click="openCcSwitchDialog(scope.row)">
                导入 CC-Switch
              </el-button>
              <el-button size="mini" type="info" @click="openEditDialog(scope.row)">编辑</el-button>
              <el-button size="mini" type="danger" @click="confirmDelete(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="apiKeys.length" class="api-key-mobile-list">
        <article v-for="item in apiKeys" :key="item.id || item.apiKey" class="api-key-mobile-card">
          <div class="api-key-mobile-card__header">
            <strong>{{ item.keyName || '-' }}</strong>
            <el-tag
              class="api-key-mobile-card__group-tag"
              :type="item.groupType === 'MONTH_CARD' ? 'warning' : 'success'"
              size="small"
              :title="getKeyGroupLabel(item)"
            >
              {{ getGroupLabel(item.groupType) }}
            </el-tag>
          </div>

          <div class="api-key-mobile-card__key-row">
            <span class="api-key-text">{{ maskApiKey(item.apiKey) }}</span>
            <el-button
              type="text"
              icon="el-icon-copy-document"
              @click="copyText(item.apiKey, '密钥已复制')"
            ></el-button>
          </div>

          <div class="api-key-mobile-card__meta">
            <div>
              <span>今日用量</span>
              <strong>{{ formatAmount(item.usedAmount) }}</strong>
            </div>
            <div>
              <span>近 30 天用量</span>
              <strong>{{ formatAmount(item.recent30DayUsedAmount) }}</strong>
            </div>
            <div>
              <span>创建时间</span>
              <strong>{{ formatDate(item.createTime) }}</strong>
            </div>
          </div>

          <div class="api-key-mobile-card__actions">
            <el-button size="mini" type="success" icon="el-icon-key" @click="openKeyUsageDialog(item)">
              一键配置 Codex
            </el-button>
            <el-button size="mini" type="primary" icon="el-icon-upload2" @click="openCcSwitchDialog(item)">
              导入 CC-Switch
            </el-button>
            <el-button size="mini" type="info" @click="openEditDialog(item)">编辑</el-button>
            <el-button size="mini" type="danger" @click="confirmDelete(item)">删除</el-button>
          </div>
        </article>
      </div>
      <div v-else-if="!loading" class="api-key-mobile-empty">暂无 API 密钥</div>

      <div class="key-pagination-note">
        显示 1 至 {{ apiKeys.length }}，共 {{ apiKeys.length }} 条结果
      </div>
    </article>

    <section class="info-grid">
      <article class="dashboard-panel">
        <h3>密钥分组</h3>
        <p>余额分组用于余额扣费，日卡分组用于日卡套餐内调用，后续也可以继续扩展更多分组。</p>
      </article>
      <article class="dashboard-panel">
        <h3>一键配置 Codex</h3>
        <p>“一键配置 Codex”会生成 Codex 的配置文件和一键安装脚本，Windows 下载 `.bat`，MacOS / Linux 下载 `.sh`。</p>
      </article>
    </section>

    <el-dialog
      :title="editingKeyId ? '编辑 API 密钥' : '创建 API 密钥'"
      :visible.sync="keyDialogVisible"
      width="620px"
      :close-on-click-modal="false"
    >
      <el-form ref="keyForm" :model="keyForm" :rules="keyRules" label-width="90px">
        <el-form-item label="密钥名称" prop="keyName">
          <el-input v-model.trim="keyForm.keyName" maxlength="50" placeholder="例如：Codex 主密钥"></el-input>
        </el-form-item>
        <el-form-item label="密钥分组" prop="balanceId">
          <el-select v-model="keyForm.balanceId" placeholder="请选择分组" style="width: 100%">
            <el-option
              v-for="item in groupOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="keyDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitKeyForm">
          {{ editingKeyId ? '保存' : '创建' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="模型与价格"
      :visible.sync="modelsDialogVisible"
      width="min(96vw, 960px)"
      :close-on-click-modal="false"
    >
      <ModelPriceTable :models="supportedModels" :loading="modelsLoading" />
    </el-dialog>

    <el-dialog
      title="一键配置 Codex"
      :visible.sync="usageDialogVisible"
      width="820px"
      custom-class="usage-key-dialog"
      :close-on-click-modal="false"
    >
      <div v-if="usageTargetKey" class="usage-dialog">
        <div class="usage-client-switch">
          <div class="usage-client-switch__label">
            <span>操作系统</span>
            <small>选择后会生成对应脚本</small>
          </div>
          <el-radio-group v-model="usagePlatform" size="small">
            <el-radio-button label="windows">Windows</el-radio-button>
            <el-radio-button label="mac">MacOS / Linux</el-radio-button>
          </el-radio-group>
        </div>

        <div class="usage-file-card usage-file-card--guide">
          <div class="usage-file-header">
            <div class="usage-file-title">
              <i class="el-icon-document-checked"></i>
              <span>{{ usagePlatform === 'windows' ? 'Windows 快速配置（推荐）' : 'MacOS / Linux 快速配置（推荐）' }}</span>
            </div>
          </div>
          <el-input
            type="textarea"
            :rows="5"
            readonly
            :value="buildUsageGuide()"
          ></el-input>
        </div>

        <div class="usage-dialog__warning">
          <i class="el-icon-warning-outline"></i>
          <span>导入配置后，请开启新的 Codex 会话进行测试；已打开的会话不会自动读取新配置。</span>
        </div>

        <div class="usage-file-card">
          <div class="usage-file-header">
            <div class="usage-file-title">
              <i class="el-icon-files"></i>
              <span>{{ getInstallScriptFileName() }}</span>
            </div>
            <div class="usage-file-actions">
              <el-button
                size="mini"
                type="success"
                icon="el-icon-download"
                @click="downloadInstallScript()"
              >
                下载脚本
              </el-button>
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-document-copy"
                @click="copyText(buildInstallScript(), '安装脚本已复制')"
              >
                复制脚本
              </el-button>
            </div>
          </div>
          <el-input
            type="textarea"
            :rows="20"
            readonly
            :value="buildInstallScript()"
          ></el-input>
        </div>

        <div class="usage-dialog__tip">
          下载脚本后直接执行即可。脚本只会写入当前用户目录下的 `.codex` 配置文件，并在覆盖前自动备份原文件。
        </div>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import ModelPriceTable from '../../components/ModelPriceTable.vue';
import {
  createApiKey,
  deleteApiKey,
  fetchApiKeys,
  fetchPublicSupportedModels,
  fetchAvailableKeyGroupOptions,
  fetchKeyGroupOptions,
  fetchCcSwitchConfig,
  updateApiKey
} from '../../api/keys';

export default {
  name: 'ApiKeysView',
  components: { ModelPriceTable },
  data() {
    return {
      loading: false,
      saving: false,
      apiKeys: [],
      groupOptions: [],
      queryGroupOptions: [],
      filters: {
        keyword: '',
        balanceId: ''
      },
      keyDialogVisible: false,
      usageDialogVisible: false,
      modelsDialogVisible: false,
      modelsLoading: false,
      supportedModels: [],
      editingKeyId: null,
      usagePlatform: 'windows',
      usageTargetKey: null,
      keyForm: {
        keyName: '',
        balanceId: null
      },
      keyRules: {
        keyName: [
          { required: true, message: '请输入密钥名称', trigger: 'blur' },
          { max: 50, message: '密钥名称最多 50 个字符', trigger: 'blur' }
        ],
        balanceId: [
          { required: true, message: '请选择密钥分组', trigger: 'change' }
        ]
      },
      ccSwitchConfig: {
        providerName: '',
        endpoint: '',
        model: '',
        usageEnabled: false,
        usageBaseUrl: '',
        usageScript: '',
        usageAutoInterval: ''
      }
    };
  },
  computed: {
    tableKey() {
      return String(this.apiKeys.length);
    }
  },
  created() {
    this.loadGroupOptions();
    this.loadCcSwitchConfig({ silent: true });
    this.loadKeys();
  },
  methods: {
    openModelTestPage() {
      const route = this.$router.resolve({ name: 'chat' });
      window.open(route.href, '_blank', 'noopener,noreferrer');
    },
    handleSearch() {
      this.loadKeys();
    },
    resetFilters() {
      this.filters = {
        keyword: '',
        balanceId: ''
      };
      this.loadKeys();
    },
    buildQueryParams() {
      const params = {};
      const keyword = String(this.filters.keyword || '').trim();
      if (keyword) {
        params.keyword = keyword;
      }
      if (this.filters.balanceId !== '' && this.filters.balanceId !== null && this.filters.balanceId !== undefined) {
        params.balanceId = this.filters.balanceId;
      }
      return params;
    },
    async loadKeys() {
      this.loading = true;
      try {
        const response = await fetchApiKeys(this.buildQueryParams());
        const result = response.data || {};
        if (result.code !== 0) {
          this.handleApiError(result.message);
          return;
        }
        this.apiKeys = result.data || [];
      } catch (error) {
        this.$message.error('API 密钥加载失败，请确认后端服务已启动');
      } finally {
        this.loading = false;
      }
    },
    async loadGroupOptions() {
      try {
        const [queryResponse, availableResponse] = await Promise.all([
          fetchKeyGroupOptions(),
          fetchAvailableKeyGroupOptions()
        ]);
        const queryResult = queryResponse.data || {};
        const availableResult = availableResponse.data || {};
        this.queryGroupOptions = queryResult.code === 0 && Array.isArray(queryResult.data)
          ? queryResult.data
          : [];
        this.groupOptions = availableResult.code === 0 && Array.isArray(availableResult.data)
          ? availableResult.data
          : [];
        if (!this.groupOptions.some(item => String(item.id) === String(this.keyForm.balanceId))) {
          this.keyForm.balanceId = this.groupOptions.length ? this.groupOptions[0].id : null;
        }
      } catch (error) {
        this.queryGroupOptions = [];
        this.groupOptions = [];
      }
    },
    async loadCcSwitchConfig(options = {}) {
      const silent = options.silent === true;
      try {
        const response = await fetchCcSwitchConfig();
        const result = response.data || {};
        if (result.code !== 0 || !result.data) {
          if (!silent) {
            this.$message.error('配置加载失败，请稍后重试');
          }
          return false;
        }
        this.ccSwitchConfig = {
          providerName: result.data.providerName || '',
          endpoint: this.resolveAbsoluteUrl(result.data.endpoint || '/v1'),
          model: result.data.model || '',
          usageEnabled: result.data.usageEnabled === true,
          usageBaseUrl: this.resolveAbsoluteUrl(result.data.usageBaseUrl || result.data.endpoint || '/v1'),
          usageScript: result.data.usageScript || '',
          usageAutoInterval: result.data.usageAutoInterval || ''
        };
        if (!this.ccSwitchConfig.endpoint || !this.ccSwitchConfig.model) {
          if (!silent) {
            this.$message.error('配置不完整，请检查接口地址和默认模型');
          }
          return false;
        }
        return true;
      } catch (error) {
        if (!silent) {
          this.$message.error('配置加载失败，请稍后重试');
        }
        return false;
      }
    },
    async openKeyUsageDialog(row) {
      const loaded = await this.loadCcSwitchConfig({ silent: true });
      if (!loaded) {
        this.$message.error('Codex 配置不完整，请先检查接口地址和默认模型');
        return;
      }
      this.usageTargetKey = row;
      this.usagePlatform = 'windows';
      this.usageDialogVisible = true;
    },
    openCreateDialog() {
      this.editingKeyId = null;
      this.keyForm.keyName = '';
      this.keyForm.balanceId = this.groupOptions.length ? this.groupOptions[0].id : null;
      this.keyDialogVisible = true;
      this.clearKeyFormValidate();
    },
    openEditDialog(row) {
      this.editingKeyId = row.id;
      this.keyForm.keyName = row.keyName;
      this.keyForm.balanceId = row.balanceId;
      this.keyDialogVisible = true;
      this.clearKeyFormValidate();
    },
    clearKeyFormValidate() {
      this.$nextTick(() => {
        if (this.$refs.keyForm) {
          this.$refs.keyForm.clearValidate();
        }
      });
    },
    submitKeyForm() {
      this.$refs.keyForm.validate(async valid => {
        if (!valid) {
          return;
        }
        this.saving = true;
        try {
          const response = this.editingKeyId
            ? await updateApiKey(this.editingKeyId, this.keyForm.keyName, this.keyForm.balanceId)
            : await createApiKey(this.keyForm.keyName, this.keyForm.balanceId);
          const result = response.data || {};
          if (result.code !== 0) {
            this.handleApiError(result.message);
            return;
          }
          this.keyDialogVisible = false;
          this.$message.success(this.editingKeyId ? 'API 密钥已保存' : 'API 密钥创建成功');
          this.loadKeys();
        } catch (error) {
          this.$message.error(this.editingKeyId ? '保存失败，请稍后重试' : '创建失败，请稍后重试');
        } finally {
          this.saving = false;
        }
      });
    },
    confirmDelete(row) {
      this.$confirm(`确认删除密钥「${row.keyName}」吗？删除后不可恢复。`, '删除密钥', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const response = await deleteApiKey(row.id);
        const result = response.data || {};
        if (result.code !== 0) {
          this.handleApiError(result.message);
          return;
        }
        this.$message.success('密钥已删除');
        this.loadKeys();
      }).catch(() => {});
    },
    buildCcSwitchLink(row) {
      if (!row) {
        return '';
      }
      const params = new URLSearchParams({
        resource: 'provider',
        app: row.app,
        name: this.ccSwitchConfig.providerName || row.keyName,
        endpoint: this.ccSwitchConfig.endpoint,
        apiKey: row.apiKey,
        api_key: row.apiKey,
        key: row.apiKey,
        authKey: row.apiKey,
        model: this.ccSwitchConfig.model,
        wireApi: 'responses',
        wire_api: 'responses',
        requiresOpenAIAuth: 'true',
        requires_openai_auth: 'true',
        enabled: 'true'
      });
      if (this.ccSwitchConfig.usageEnabled) {
        params.set('usageEnabled', 'true');
        params.set('usageBaseUrl', this.ccSwitchConfig.usageBaseUrl);
        params.set('usageApiKey', row.apiKey);
        params.set('usageScript', this.toUrlSafeBase64(this.ccSwitchConfig.usageScript));
        params.set('usageAutoInterval', String(this.ccSwitchConfig.usageAutoInterval));
      }
      return 'ccswitch://v1/import?' + params.toString();
    },
    async openCcSwitchDialog(row) {
      const loaded = await this.loadCcSwitchConfig();
      if (!loaded) {
        return;
      }
      if (!row.app || !this.ccSwitchConfig.providerName || !this.ccSwitchConfig.endpoint || !this.ccSwitchConfig.model) {
        this.$message.error('CC Switch 配置不完整，请稍后重试');
        return;
      }
      if (this.ccSwitchConfig.usageEnabled
        && (!this.ccSwitchConfig.usageBaseUrl || !this.ccSwitchConfig.usageScript || !this.ccSwitchConfig.usageAutoInterval)) {
        this.$message.error('CC Switch 配置不完整，请稍后重试');
        return;
      }
      const link = this.buildCcSwitchLink(row);
      if (!link) {
        this.$message.error('CC Switch 导入链接生成失败');
        return;
      }
      window.location.href = link;
      this.$message.success('已尝试打开 CC Switch，请在系统弹窗中确认导入');
    },
    toUrlSafeBase64(text) {
      const value = String(text || '');
      if (!value) {
        return '';
      }
      const bytes = new TextEncoder().encode(value);
      let binary = '';
      bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
      });
      return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
    },
    resolveAbsoluteUrl(path) {
      const value = String(path || '').trim();
      if (!value) {
        return new URL('/v1', window.location.origin).toString().replace(/\/$/, '');
      }
      try {
        return new URL(value, window.location.origin).toString().replace(/\/$/, '');
      } catch (error) {
        return value;
      }
    },
    getUsageBaseUrl() {
      return this.ccSwitchConfig.endpoint || this.resolveAbsoluteUrl('/v1');
    },
    getUsageModel() {
      return this.ccSwitchConfig.model || '';
    },
    getPlatformLabel() {
      return this.usagePlatform === 'mac' ? 'MacOS / Linux' : 'Windows';
    },
    getCodexConfigPath() {
      return this.usagePlatform === 'mac'
        ? '~/.codex/config.toml'
        : '%USERPROFILE%\\.codex\\config.toml';
    },
    async openModelsDialog() {
      this.modelsDialogVisible = true;
      if (this.supportedModels.length) {
        return;
      }
      this.modelsLoading = true;
      try {
        const response = await fetchPublicSupportedModels();
        const result = response.data || {};
        if (result.code !== 0) {
          this.handleApiError(result.message);
          return;
        }
        this.supportedModels = Array.isArray(result.data) ? result.data : [];
      } catch (error) {
        this.$message.error('模型价格加载失败，请稍后重试');
      } finally {
        this.modelsLoading = false;
      }
    },
    getCodexAuthPath() {
      return this.usagePlatform === 'mac'
        ? '~/.codex/auth.json'
        : '%USERPROFILE%\\.codex\\auth.json';
    },
    getInstallScriptFileName() {
      const keyName = this.usageTargetKey
        ? (this.usageTargetKey.keyName || `key-${this.usageTargetKey.id || 'config'}`)
        : 'codex';
      const baseName = this.sanitizeFileName(keyName);
      return this.usagePlatform === 'mac'
        ? `${baseName}-codex-install.sh`
        : `${baseName}-codex-install.bat`;
    },
    buildSelectedCodexConfig() {
      return this.buildCodexConfig(this.usageTargetKey);
    },
    buildCodexConfig(row) {
      if (!row) {
        return '';
      }
      const model = this.escapeTomlString(this.getUsageModel());
      const baseUrl = this.escapeTomlString(this.getUsageBaseUrl());
      const token = this.escapeTomlString(row.apiKey || '');
      const lines = [
        'model_provider = "MyOpenAI"',
        `model = "${model}"`,
        `review_model = "${model}"`,
        'model_reasoning_effort = "high"',
        'disable_response_storage = true',
        'network_access = "enabled"',
        'windows_wsl_setup_acknowledged = true',
        '',
        '[model_providers.MyOpenAI]',
        'name = "MyOpenAI"',
        `base_url = "${baseUrl}"`,
        'wire_api = "responses"',
        'requires_openai_auth = false',
        'http_headers = { "x-openai-actor-authorization" = "local-image-extension" }',
        `experimental_bearer_token = "${token}"`
      ];
      lines.push('');
      lines.push('[features]');
      lines.push('goals = true');
      return lines.join('\n');
    },
    buildCodexAuth(row) {
      if (!row) {
        return '';
      }
      return JSON.stringify({
        OPENAI_API_KEY: row.apiKey || ''
      }, null, 2);
    },
    buildInstallScript() {
      return this.usagePlatform === 'mac'
        ? this.buildMacInstallScript()
        : this.buildWindowsInstallScript();
    },
    buildWindowsInstallScript() {
      const configLines = this.toBatchEchoLines(this.buildSelectedCodexConfig());
      const authLines = this.toBatchEchoLines(this.buildCodexAuth(this.usageTargetKey));
      const lines = [
        '@echo off',
        'setlocal',
        'chcp 65001 >nul 2>nul',
        'echo ============================================================',
        'echo Running file: %~f0',
        'echo ============================================================',
        'echo.',
        'echo Codex setup',
        'echo.',
        'set "CODEX_DIR=%USERPROFILE%\\.codex"',
        'if not exist "%CODEX_DIR%" (',
        '  echo Creating Codex config directory...',
        '  mkdir "%CODEX_DIR%"',
        '  if errorlevel 1 (',
        '    echo Failed to create Codex config directory.',
        '    pause',
        '    exit /b 1',
        '  )',
        ')',
        'call :backup_file "config.toml"',
        'if errorlevel 1 (',
        '  pause',
        '  exit /b 1',
        ')',
        'call :backup_file "auth.json"',
        'if errorlevel 1 (',
        '  pause',
        '  exit /b 1',
        ')',
        'echo Writing config.toml...',
        '> "%CODEX_DIR%\\config.toml" (',
        configLines,
        ')',
        'if errorlevel 1 (',
        '  echo Failed to write config.toml.',
        '  pause',
        '  exit /b 1',
        ')',
        'echo Writing auth.json...',
        '> "%CODEX_DIR%\\auth.json" (',
        authLines,
        ')',
        'if errorlevel 1 (',
        '  echo Failed to write auth.json.',
        '  pause',
        '  exit /b 1',
        ')',
        'echo ============================================================',
        'echo               配置完成，请认真阅读以下提示信息',
        'echo.',
        'echo   请先完全退出并重新启动 Codex，然后在 Codex 中新建会话再使用；否则可能遇到网络连接失败等问题。',
        'echo.',
        'echo ============================================================',
        'echo 按任意键关闭窗口。',
        'pause >nul',
        'endlocal',
        'exit /b 0',
        '',
        ':backup_file',
        'set "BACKUP_SOURCE=%CODEX_DIR%\\%~1"',
        'if not exist "%BACKUP_SOURCE%" exit /b 0',
        'set "BACKUP_TARGET=%BACKUP_SOURCE%.bak"',
        'if not exist "%BACKUP_TARGET%" goto backup_copy',
        'set /A BACKUP_INDEX=1',
        ':backup_next',
        'set "BACKUP_TARGET=%BACKUP_SOURCE%.bak.%BACKUP_INDEX%"',
        'if not exist "%BACKUP_TARGET%" goto backup_copy',
        'set /A BACKUP_INDEX+=1',
        'goto backup_next',
        ':backup_copy',
        'echo Backing up %~1...',
        'copy /Y "%BACKUP_SOURCE%" "%BACKUP_TARGET%" >nul',
        'if errorlevel 1 (',
        '  echo Failed to back up %~1.',
        '  exit /b 1',
        ')',
        'echo Backup created: "%BACKUP_TARGET%"',
        'exit /b 0'
      ].join('\r\n');
      return lines.split('\r\n').map(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine || trimmedLine.startsWith(':') || trimmedLine === ')' || trimmedLine.startsWith('@')) {
          return line;
        }
        return line.replace(/^(\s*)/, '$1@');
      }).join('\r\n');
    },
    buildMacInstallScript() {
      return [
        'set -eu',
        '',
        'printf "============================================================\\n"',
        'printf "当前运行文件：%s\\n" "$0"',
        'printf "============================================================\\n\\n"',
        'echo "Codex 一键配置"',
        'echo "此脚本只会写入 \\"$HOME/.codex\\" 目录下的配置文件。"',
        'echo "脚本不会联网、不会申请管理员权限，也不会修改系统设置。"',
        'echo',
        'CODEX_DIR="$HOME/.codex"',
        'if [ ! -d "$CODEX_DIR" ]; then',
        '  echo "正在创建 \\"$CODEX_DIR\\"..."',
        '  mkdir -p "$CODEX_DIR"',
        'fi',
        '',
        'backup_file() {',
        '  BACKUP_SOURCE="$CODEX_DIR/$1"',
        '  if [ ! -f "$BACKUP_SOURCE" ]; then',
        '    return 0',
        '  fi',
        '  BACKUP_TARGET="$BACKUP_SOURCE.bak"',
        '  if [ -e "$BACKUP_TARGET" ]; then',
        '    BACKUP_INDEX=1',
        '    while [ -e "$BACKUP_SOURCE.bak.$BACKUP_INDEX" ]; do',
        '      BACKUP_INDEX=$((BACKUP_INDEX + 1))',
        '    done',
        '    BACKUP_TARGET="$BACKUP_SOURCE.bak.$BACKUP_INDEX"',
        '  fi',
        '  echo "正在备份 $1..."',
        '  cp "$BACKUP_SOURCE" "$BACKUP_TARGET"',
        '  echo "已备份到 \\"$BACKUP_TARGET\\""',
        '}',
        '',
        'backup_file "config.toml"',
        'backup_file "auth.json"',
        '',
        'echo "正在写入 config.toml..."',
        'cat > "$CODEX_DIR/config.toml" <<\'__CODEX_CONFIG__\'',
        this.buildSelectedCodexConfig(),
        '__CODEX_CONFIG__',
        '',
        'echo "正在写入 auth.json..."',
        'cat > "$CODEX_DIR/auth.json" <<\'__CODEX_AUTH__\'',
        this.buildCodexAuth(this.usageTargetKey),
        '__CODEX_AUTH__',
        '',
        'printf "\\n============================================================\\n"',
        'printf "              配置完成，请认真阅读以下提示信息\\n\\n"',
        'printf "  请先完全退出并重新启动 Codex，然后在 Codex 中新建会话再使用；否则可能遇到网络连接失败等问题。\\n\\n"',
        'printf "============================================================\\n"',
        'printf "按回车键关闭窗口。\\n"',
        'read -r _'
      ].join('\n');
    },
    buildUsageGuide() {
      if (this.usagePlatform === 'windows') {
        return [
          '自动备份已有 Codex 配置，并写入当前 API 密钥配置。',
          '',
          '1、Windows 用户：下载脚本后直接运行。',
          '2、脚本只会修改当前用户目录下的 .codex 配置文件。',
          '3、脚本不会联网、不会申请管理员权限，也不会修改系统设置。'
        ].join('\n');
      }
      if (this.usagePlatform === 'mac') {
        return [
          '复制配置命令，在终端中执行后自动备份并写入 Codex 配置。',
          '',
          '1、MacOS：按 Command + Space，搜索“终端”，然后按回车。',
          '2、Linux：从应用菜单中打开“终端”。',
          '3、将下方命令粘贴到终端并按回车执行。'
        ].join('\n');
      }
      const lines = [
        `1. 下载脚本：${this.getInstallScriptFileName()}`,
        `2. 脚本会写入：${this.getCodexConfigPath()}`,
        `3. 脚本会写入：${this.getCodexAuthPath()}`,
        '4. 覆盖前会先自动备份原文件'
      ];
      if (this.usagePlatform === 'mac') {
        lines.push(`5. 执行方式：sh "${this.getInstallScriptFileName()}"`);
      } else {
        lines.push(`5. 执行方式：双击运行，或在 cmd 中执行 "${this.getInstallScriptFileName()}"`);
      }
      lines.push('6. 配置完成后，重启 Codex 并新建会话。');
      return lines.join('\n');
    },
    sanitizeFileName(value) {
      return String(value || 'codex')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase() || 'codex';
    },
    toBatchEchoLines(text) {
      return String(text || '')
        .split('\n')
        .map(line => line ? `  echo ${this.escapeBatchLine(line)}` : '  echo.')
        .join('\r\n');
    },
    escapeBatchLine(value) {
      return String(value || '')
        .replace(/\^/g, '^^')
        .replace(/%/g, '%%')
        .replace(/&/g, '^&')
        .replace(/\|/g, '^|')
        .replace(/</g, '^<')
        .replace(/>/g, '^>');
    },
    downloadInstallScript() {
      const content = this.buildInstallScript();
      const fileName = this.getInstallScriptFileName();
      const isWindows = this.usagePlatform === 'windows';
      const blob = new Blob([content], {
        type: isWindows ? 'text/plain;charset=utf-8' : 'text/x-shellscript;charset=utf-8'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.$message.success('安装脚本已下载');
    },
    escapeTomlString(value) {
      return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    },
    copyText(text, successMessage) {
      if (!text) {
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success(successMessage);
        }).catch(() => {
          this.copyByFallback(text, successMessage);
        });
        return;
      }
      this.copyByFallback(text, successMessage);
    },
    copyByFallback(text, successMessage) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.$message.success(successMessage);
    },
    formatAmount(value) {
      return '$' + Number(value || 0).toFixed(4);
    },
    getKeyGroupLabel(key) {
      if (!key) {
        return '';
      }
      const matchedOption = this.queryGroupOptions.find(item => String(item.id) === String(key.balanceId));
      if (matchedOption && matchedOption.name) {
        return matchedOption.name;
      }
      const balanceId = key.balanceId ? `#${key.balanceId}` : '';
      return this.getGroupLabel(key.groupType) + balanceId;
    },
    getGroupLabel(groupType) {
      if (groupType === 'MONTH_CARD') {
        return '日卡分组';
      }
      if (groupType === 'BALANCE') {
        return '余额分组';
      }
      return groupType || '';
    },
    maskApiKey(apiKey) {
      if (!apiKey || apiKey.length <= 16) {
        return apiKey || '';
      }
      return apiKey.slice(0, 8) + '...' + apiKey.slice(-6);
    },
    formatDate(value, withSeconds = false) {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const pad = number => String(number).padStart(2, '0');
      const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
      const seconds = withSeconds ? `:${pad(date.getSeconds())}` : '';
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${time}${seconds}`;
    },
    handleApiError(message) {
      const messages = {
        LOGIN_REQUIRED: '请先登录',
        TOKEN_INVALID: '登录状态已失效，请重新登录',
        TOKEN_EXPIRED: '登录已过期，请重新登录',
        KEY_NAME_REQUIRED: '请输入密钥名称',
        KEY_NAME_TOO_LONG: '密钥名称最多 50 个字符',
        KEY_GROUP_REQUIRED: '请选择密钥分组',
        INVALID_KEY_GROUP: '密钥分组不正确',
        KEY_NOT_FOUND: '密钥不存在'
      };
      this.$message.error(messages[message] || message || '操作失败');
      if (message === 'LOGIN_REQUIRED' || message === 'TOKEN_INVALID' || message === 'TOKEN_EXPIRED') {
        window.location.href = '/login';
      }
    }
  }
};
</script>

<style scoped>
.key-action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.key-usage-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.usage-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.usage-client-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #deebe6;
  border-radius: 8px;
  flex-wrap: wrap;
}

.usage-client-switch__label {
  display: grid;
  gap: 3px;
}

.usage-client-switch__label span {
  color: #213846;
  font-size: 14px;
  font-weight: 700;
}

.usage-client-switch__label small {
  color: #8799a4;
  font-size: 12px;
}

.usage-client-switch .el-radio-group {
  display: inline-flex;
  padding: 3px;
  background: #f3f8f6;
  border: 1px solid #dcebe6;
  border-radius: 7px;
}

.usage-client-switch .el-radio-button__inner {
  padding: 8px 14px;
  color: #526b78;
  background: transparent;
  border: 0;
  border-radius: 5px;
  box-shadow: none;
  font-weight: 600;
}

.usage-client-switch .el-radio-button:first-child .el-radio-button__inner,
.usage-client-switch .el-radio-button:last-child .el-radio-button__inner {
  border: 0;
  border-radius: 5px;
}

.usage-client-switch .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  color: #0f856e;
  background: #ffffff;
  border-color: transparent;
  box-shadow: 0 1px 4px rgba(24, 86, 71, 0.12);
}

.usage-file-card {
  padding: 14px;
  background: #ffffff;
  border: 1px solid #deebe6;
  border-radius: 8px;
}

.usage-file-card--guide {
  background: #fbfefd;
}

.usage-file-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #213846;
  font-size: 14px;
  font-weight: 700;
}

.usage-file-title {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
}

.usage-file-title i {
  color: #0f856e;
  font-size: 16px;
}

.usage-file-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.usage-file-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.usage-file-card .el-textarea__inner {
  min-height: 0;
  padding: 12px 13px;
  color: #2f4a57;
  background: #f7fbfa;
  border-color: #dcebe6;
  border-radius: 6px;
  font-family: Consolas, "Courier New", monospace;
  font-size: 12px;
  line-height: 1.65;
  resize: vertical;
}

.usage-file-card--guide .el-textarea__inner {
  color: #526b78;
  background: #ffffff;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.75;
}

.usage-dialog__tip {
  padding: 10px 12px;
  color: #637783;
  background: #fffaf0;
  border: 1px solid #f0dfaa;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.7;
}

.usage-dialog__warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 11px 12px;
  color: #b42318;
  background: #fff1f0;
  border: 1px solid #f2c4c0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.7;
}

.usage-dialog__warning i {
  flex: 0 0 auto;
  margin-top: 3px;
  font-size: 15px;
}

.api-key-mobile-list,
.api-key-mobile-empty {
  display: none;
}

@media (max-width: 640px) {
  .section-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .section-actions .el-button {
    width: 100%;
    margin-left: 0;
  }

  .key-filter-bar {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .key-filter-bar .usage-filter-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
  }

  .key-filter-bar .usage-filter-actions .el-button {
    width: 100%;
    margin-left: 0;
  }

  .api-key-table {
    display: none;
  }

  .api-key-mobile-list {
    display: grid;
    gap: 12px;
  }

  .api-key-mobile-card {
    display: grid;
    gap: 12px;
    padding: 14px;
    background: #ffffff;
    border: 1px solid #deebe6;
    border-radius: 8px;
  }

  .api-key-mobile-card__header,
  .api-key-mobile-card__key-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
  }

  .api-key-mobile-card__header strong {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    color: #17303b;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .api-key-mobile-card__group-tag {
    min-width: 0;
    max-width: 52%;
    flex: 0 1 52%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .api-key-mobile-card__key-row .api-key-text {
    min-width: 0;
    flex: 1 1 auto;
  }

  .api-key-mobile-card__key-row .el-button {
    flex: 0 0 auto;
    margin-left: 0;
    padding: 6px;
  }

  .api-key-mobile-card__meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding-top: 12px;
    border-top: 1px solid #e5efeb;
  }

  .api-key-mobile-card__meta div {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  .api-key-mobile-card__meta div:last-child {
    grid-column: 1 / -1;
  }

  .api-key-mobile-card__meta span {
    color: #718590;
    font-size: 12px;
  }

  .api-key-mobile-card__meta strong {
    overflow: hidden;
    color: #17303b;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .api-key-mobile-card__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .api-key-mobile-card__actions .el-button {
    width: 100%;
    margin-left: 0;
  }

  .api-key-mobile-empty {
    padding: 24px 12px;
    color: #748894;
    text-align: center;
  }

  .usage-client-switch {
    align-items: flex-start;
    flex-direction: column;
  }

  .usage-client-switch .el-radio-group {
    width: 100%;
  }

  .usage-client-switch .el-radio-button {
    flex: 1 1 50%;
  }

  .usage-client-switch .el-radio-button__inner {
    width: 100%;
    padding-right: 8px;
    padding-left: 8px;
  }

  .usage-file-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .usage-file-actions {
    width: 100%;
  }

  .usage-file-actions .el-button {
    flex: 1 1 50%;
  }
}
</style>
